import { defineStore } from "pinia"
import { adminBackendClient } from "@/services/adminBackendClient"
import { useProjectStore } from "./projectStore"
import { AxiosError } from "axios"
import { Overview } from "@/types/overview"
import {
   CombinationExplorer,
   TimeSeries,
   ConversionTimeSeries,
} from "@/types/combination-explorer"
import {
   HourOfDay,
   TopN,
   VariableExplorer,
   VariableSnapshot,
} from "@/types/dashboard"
import { collectError } from "@utils/errors"
import { toast } from "vue3-toastify"
import overview from "@/mocks/overview"
import combinationExplorer from "@/mocks/combination-explorer"
import variableExplorer from "@/mocks/variable-explorer"
import { ALL_UTM_SOURCES_OPTION } from "@/utils/constants"

// Helper function to handle mock timeout errors
function shouldMockTimeout(): boolean {
   if (import.meta.env.VITE_MOCK_SESSION_STATS_TIMEOUT !== "true") {
      return false
   }

   return true
}

function isTimeoutError(error: AxiosError): boolean {
   try {
      return error.code === "ECONNABORTED"
   } catch (_error) {
      return false
   }
}

interface AnalyticsState {
   error: string | null
   start_date: number
   end_date: number
   overview: Overview | null
   loading: boolean
   utmSource: string
   topN: TopN | null
   timeSeries: TimeSeries | null
   conversionTimeSeries: ConversionTimeSeries | null
   variableSnapshot: VariableSnapshot | null
   hourOfDay: HourOfDay[] | null
   selectedVariableId: number | null
}

// Helper functions for default date values
function getDefaultStartDate(): number {
   // Get 7 days ago from today at midnight local time
   const today = new Date()
   const startDate = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 7, 0, 0, 0, 0)
   return startDate.getTime()
}

function getDefaultEndDate(): number {
   // Get today at 11:59:59 PM local time
   const today = new Date()
   const endDate = new Date(today.getFullYear(), today.getMonth(), today.getDate(), 23, 59, 59, 999)
   return endDate.getTime()
}

export const useAnalyticsStore = defineStore("analyticsStore", {
   state: (): AnalyticsState => ({
      error: null,
      start_date: getDefaultStartDate(), // default to 7 days ago
      end_date: getDefaultEndDate(),
      overview: null,
      loading: false,
      utmSource: ALL_UTM_SOURCES_OPTION,
      topN: null,
      timeSeries: null,
      conversionTimeSeries: null,
      variableSnapshot: null,
      hourOfDay: null,
      selectedVariableId: null,
   }),

   getters: {
      dateRange: (state) => ({
         start: state.start_date,
         end: state.end_date,
      }),
   },

   actions: {
      setSelectedVariableId(variableId: number | undefined) {
         this.selectedVariableId = variableId ?? null
      },
      async callForOverview(
         startDate: number,
         endDate: number,
         utmSource: string,
      ) {
         const projectStore = useProjectStore()
         const params: Record<string, unknown> = {
            start_date: Math.floor(startDate / 1000), // API uses seconds, JS/TS use milliseconds
            end_date: Math.floor(endDate / 1000), // API uses seconds, JS/TS use milliseconds
         }

         if (utmSource !== ALL_UTM_SOURCES_OPTION) {
            params.utm_source = utmSource
         }

         return await adminBackendClient.get<Overview>(
            `/v2/projects/${projectStore.projectId}/overview`,
            { params },
         )
      },
      async fetchOverview(): Promise<Overview | null> {
         const projectStore = useProjectStore()
         if (!projectStore.projectId) {
            throw new Error("No active project selected")
         }

         this.error = null
         this.loading = true
         this.overview = null

         try {
            let responseData: Overview | null = null
            if (shouldMockTimeout()) {
               await new Promise((resolve) => setTimeout(resolve, 5000))
               toast.error(
                  "Request took longer than expected. Please try refreshing.",
               )
               return null
            }
            if (import.meta.env.VITE_MOCK_SESSION_STATS == "true") {
               // simulate network delay
               await new Promise((resolve) =>
                  setTimeout(resolve, Math.random() * 1000 + 250),
               )
               responseData = overview
            } else {
               const response = await this.callForOverview(
                  this.start_date,
                  this.end_date,
                  this.utmSource,
               )
               responseData = response.data
            }

            // Update all fields atomically to maintain reactivity
            this.$patch({
               overview: responseData,
               start_date: this.start_date,
               end_date: this.end_date,
               error: null,
            })
            return responseData
         } catch (error) {
            await collectError(error as Error)
            if (isTimeoutError(error as AxiosError)) {
               toast.error(
                  "Request took longer than expected. Please try refreshing.",
               )
            } else {
               toast.error("Failed to fetch overview data")
            }
            return null
         } finally {
            this.loading = false
         }
      },

      async callForTopN(
         startDate: number,
         endDate: number,
         utmSource: string,
         pagination?: {
            startAt: number
            rowsPerPage: number
            sortBy: string
            descending: boolean
         },
      ) {
         const projectStore = useProjectStore()
         const params: Record<string, unknown> = {
            start_date: Math.floor(startDate / 1000),
            end_date: Math.floor(endDate / 1000),
         }

         if (utmSource !== ALL_UTM_SOURCES_OPTION) {
            params.utm_source = utmSource
         }

         if (pagination) {
            params.start_at = pagination.startAt
            params.rows_per_page = pagination.rowsPerPage
            params.sort_by = pagination.sortBy
            params.descending = pagination.descending
         }

         return await adminBackendClient.get<TopN>(
            `/v2/projects/${projectStore.projectId}/top-n`,
            { params },
         )
      },

      async fetchTopN(pagination?: {
         startAt: number
         rowsPerPage: number
         sortBy: string
         descending: boolean
      }) {
         const projectStore = useProjectStore()
         if (!projectStore.projectId) {
            throw new Error("No active project selected")
         }

         this.error = null
         this.loading = true
         this.topN = null

         try {
            const response = await this.callForTopN(
               this.start_date,
               this.end_date,
               this.utmSource,
               pagination,
            )
            this.$patch({
               topN: response.data,
               loading: false,
            })
         } catch (error) {
            const message = "Failed to fetch top-n data"
            this.$patch({
               error: message,
               loading: false,
            })
            await collectError(error as Error)
            toast.error(message)
         }
      },

      async callForCombinationExplorer(
         startDate: number,
         endDate: number,
         utmSource: string,
         pagination?: {
            startAt: number
            rowsPerPage: number
            sortBy: string
            descending: boolean
         },
      ) {
         const projectStore = useProjectStore()
         const params: Record<string, unknown> = {
            start_date: Math.floor(startDate / 1000),
            end_date: Math.floor(endDate / 1000),
         }

         if (utmSource !== ALL_UTM_SOURCES_OPTION) {
            params.utm_source = utmSource
         }

         if (pagination) {
            params.start_at = pagination.startAt
            params.rows_per_page = pagination.rowsPerPage
            params.sort_by = pagination.sortBy
            params.descending = pagination.descending
         }

         return await adminBackendClient.get<CombinationExplorer>(
            `/v2/projects/${projectStore.projectId}/combination-explorer`,
            { params },
         )
      },

      async fetchCombinationExplorer(pagination?: {
         startAt: number
         rowsPerPage: number
         sortBy: string
         descending: boolean
      }): Promise<CombinationExplorer | null> {
         const projectStore = useProjectStore()
         if (!projectStore.projectId) {
            throw new Error("No active project selected")
         }

         this.error = null
         this.loading = true
         this.topN = null
         this.timeSeries = null
         this.conversionTimeSeries = null

         try {
            let responseData: CombinationExplorer | null = null
            if (shouldMockTimeout()) {
               await new Promise((resolve) => setTimeout(resolve, 5000))
               toast.error(
                  "Request took longer than expected. Please try refreshing.",
               )
               return null
            }
            if (import.meta.env.VITE_MOCK_SESSION_STATS == "true") {
               // simulate network delay
               await new Promise((resolve) =>
                  setTimeout(resolve, Math.random() * 1000 + 1000),
               )
               responseData = combinationExplorer
            } else {
               const result = await this.callForCombinationExplorer(
                  this.start_date,
                  this.end_date,
                  this.utmSource,
                  pagination,
               )
               responseData = result.data
            }

            // Update all fields atomically to maintain reactivity
            this.$patch({
               topN: responseData?.top_n,
               timeSeries: responseData?.time_series,
               conversionTimeSeries: responseData?.conversion_time_series,
               start_date: this.start_date,
               end_date: this.end_date,
               error: null,
               loading: false,
            })
            return responseData
         } catch (error) {
            await collectError(error as Error)
            if (isTimeoutError(error as AxiosError)) {
               toast.error(
                  "Request took longer than expected. Please try refreshing.",
               )
            } else {
               toast.error("Failed to fetch combination explorer data")
            }
            return null
         } finally {
            this.loading = false
         }
      },

      async callForVariableExplorer(
         startDate: number,
         endDate: number,
         variableId: number,
         utmSource: string,
      ) {
         const projectStore = useProjectStore()
         const params: Record<string, unknown> = {
            start_date: Math.floor(startDate / 1000),
            end_date: Math.floor(endDate / 1000),
            variable_id: variableId,
         }

         if (utmSource !== ALL_UTM_SOURCES_OPTION) {
            params.utm_source = utmSource
         }

         return await adminBackendClient.get<VariableExplorer>(
            `/v2/projects/${projectStore.projectId}/variable-explorer`,
            { params },
         )
      },

      async fetchVariableExplorer(
         variableId: number,
      ): Promise<VariableExplorer | null> {
         const projectStore = useProjectStore()
         if (!projectStore.projectId) {
            throw new Error("No active project selected")
         }

         this.error = null
         this.loading = true
         this.variableSnapshot = null
         this.hourOfDay = null

         try {
            let responseData: VariableExplorer | null = null
            if (shouldMockTimeout()) {
               await new Promise((resolve) => setTimeout(resolve, 5000))
               toast.error(
                  "Request took longer than expected. Please try refreshing.",
               )
               return null
            }
            if (import.meta.env.VITE_MOCK_SESSION_STATS == "true") {
               // simulate network delay
               await new Promise((resolve) =>
                  setTimeout(resolve, Math.random() * 1000 + 250),
               )
               responseData = variableExplorer
            } else {
               const response = await this.callForVariableExplorer(
                  this.start_date,
                  this.end_date,
                  variableId,
                  this.utmSource,
               )
               responseData = response.data
            }

            // Update all fields atomically to maintain reactivity
            this.$patch({
               variableSnapshot: responseData.variable_snapshot,
               hourOfDay: responseData.hour_of_day,
               error: null,
            })
            return responseData
         } catch (error) {
            await collectError(error as Error)
            if (isTimeoutError(error as AxiosError)) {
               toast.error(
                  "Request took longer than expected. Please try refreshing.",
               )
            } else {
               toast.error("Failed to fetch variable explorer data")
            }
            return null
         } finally {
            this.loading = false
         }
      },

      async loadUtmSources() {
         try {
            let response = null
            if (import.meta.env.VITE_MOCK_SESSION_STATS == "true") {
               response = { data: ["google", "facebook", "instagram", "bing"] }
            } else {
               const projectStore = useProjectStore()
               if (!projectStore.projectId) {
                  throw new Error("No active project selected")
               }
               response = await adminBackendClient.get(
                  `/v2/projects/${projectStore.projectId}/field-values`,
                  {
                     params: {
                        field_name: "utm_source",
                        start_date: Math.floor(this.start_date / 1000), // API uses seconds, JS/TS use milliseconds
                        end_date: Math.floor(this.end_date / 1000), // API uses seconds, JS/TS use milliseconds
                     },
                  },
               )
            }
            return response.data as string[]
         } catch (error) {
            collectError(error as Error)
            toast.error("Failed to load UTM sources")
            return [] as string[]
         }
      },

      async loadUtmSourcesAc(prefix: string) {
         try {
            let response = null
            if (import.meta.env.VITE_MOCK_SESSION_STATS == "true") {
               response = {
                  data: ["google", "facebook", "instagram", "bing"].filter(
                     (v) => v.toLocaleLowerCase().indexOf(prefix) > -1,
                  ),
               }
            } else {
               const projectStore = useProjectStore()
               if (!projectStore.projectId) {
                  throw new Error("No active project selected")
               }
               response = await adminBackendClient.get(
                  `/v2/projects/${projectStore.projectId}/field-suggestions`,
                  {
                     params: {
                        field_name: "utm_source",
                        start_date: Math.floor(this.start_date / 1000), // API uses seconds, JS/TS use milliseconds
                        end_date: Math.floor(this.end_date / 1000), // API uses seconds, JS/TS use milliseconds
                        prefix: prefix,
                     },
                  },
               )
            }
            return response.data as string[]
         } catch (error) {
            collectError(error as Error)
            toast.error("Failed to load UTM sources")
            return [] as string[]
         }
      },
      updateDateRange(startDate: number, endDate: number) {
         this.start_date = startDate
         this.end_date = endDate
      },

      clearState() {
         this.error = null
         this.start_date = getDefaultStartDate()
         this.end_date = getDefaultEndDate()
         this.overview = null
         this.topN = null
         this.timeSeries = null
         this.conversionTimeSeries = null
         this.loading = false
         this.variableSnapshot = null
         this.hourOfDay = null
         this.selectedVariableId = 0
      },
   },
   persist: true,
})
